import { sendTextWebSocketRequest } from "./websocketManager";
import { buildOrchestrationProcessPayload } from "../../utils/orchestrationPayload";
import { devError } from "../../utils/safeClientLog";

export { ORCHESTRATION_SESSION_ID_KEY, getStoredOrchestrationSessionId, setStoredOrchestrationSessionId } from "./orchestrationSession";

// Define the payload interface for AI prescription text parsing
export interface AIPrescriptionPayload {
  text: string;
  patientUniqueIdentifier: string;
  /** Optional patient display name used for PHI masking context in orchestration. */
  patientName?: string;
  jwtToken: string;
  /** Server draft key; usually from WebSocket `connected` (stored) or prior HTTP response. */
  sessionId?: string;
  doctorId: string;
  providerLocationId: string;
  /** Prescriber login id for scheduled-drug MFA (orchestration `loginUserId`). Must be `localStorage` `LoginUserID`, not `doctorId`. */
  loginUserId?: string;
  /** Raw WebVTT file body when user attached a .vtt transcript */
  vttContent?: string;
  /** Exact pharmacy chip row selected by the user, when applicable. */
  selectedPharmacy?: {
    pharmacyId?: string;
    pharmacyName?: string;
    medicationName?: string;
  };
  pharmacyId?: string;
  pharmacyName?: string;
  /**
   * Full structured favourite-drug chip object, when the user picked one (drugId,
   * dispensibleDrugId, personDrugId, pharmacyId, pharmacyName, strength, route, dosageForm,
   * sig, quantity, refills, ...). Lets the backend build a ready-to-confirm draft directly
   * instead of re-deriving it from the reconstructed `text` via extract_medication_info +
   * get_search_pharmacies.
   */
  favouriteDrug?: Record<string, unknown>;
  /** Per-prescription EPA / Priority Indicator selections, keyed by medication name. */
  prescriptionFlags?: Record<
    string,
    { priorAuthorization: boolean; priorityIndicator: boolean; epcsQueued?: boolean }
  >;
  /**
   * Structural proof this turn came from a click on the prescription summary card's Send /
   * Send all button. Set ONLY by those two onClick handlers (PrescriptionSummary/index.tsx)
   * via sendOriginStore.ts -- never by the chat box or voice input. Backend gate:
   * SEND_THROUGH_BUTTON_ONLY (orchestration_agent's agents/safeguards.py).
   */
  sendOrigin?: "button";
}

// Define the payload interface for preferred pharmacy
// export interface PreferredPharmacyPayload {
//   patientUniqueIdentifier: string;
//   jwtToken: string;
// }

// Define the payload interface for sending prescription to pharmacy
export interface SendPrescriptionToPharmacyPayload { 
  jwtToken: string;
}

// AI text parsing API endpoint - can be called multiple times
// Now uses WebSocket instead of HTTP POST
export const parseAIPrescriptionText = (data: AIPrescriptionPayload) => {
  const payload = buildOrchestrationProcessPayload({
    type: "process",
    text: data.text,
    patientUniqueIdentifier: data.patientUniqueIdentifier,
    patientName: data.patientName,
    doctorId: data.doctorId,
    providerLocationId: data.providerLocationId,
    sessionId: data.sessionId,
    vttContent: data.vttContent,
    loginUserId: data.loginUserId,
    jwtToken: data.jwtToken,
    selectedPharmacy: data.selectedPharmacy,
    pharmacyId: data.pharmacyId,
    pharmacyName: data.pharmacyName,
    favouriteDrug: data.favouriteDrug,
    prescriptionFlags: data.prescriptionFlags,
    sendOrigin: data.sendOrigin,
  });

  return sendTextWebSocketRequest(payload)
    .then((response) => response)
    .catch((error) => {
      devError("parseAIPrescriptionText failed");
      throw error;
    });
};

// // Preferred Pharmacy API endpoint
// export const getPreferredPharmacy = (data: PreferredPharmacyPayload) => {
//   return post("https://mcpserver.h2hdigitalrx.com/api/pref_pharma", { 
//     patientUniqueIdentifier: data.patientUniqueIdentifier,  
//     jwtToken: data.jwtToken, 
//   });
// };


// Send Prescription to Pharmacy API endpoint
// export const sendPrescriptionToPharmacy = (data: SendPrescriptionToPharmacyPayload) => {
//   return post("https://mcpserver.h2hdigitalrx.com/api/presc_send", {  
//     jwtToken: data.jwtToken, 
//   });
// };  